import { Base } from "@app/controllers/base"
import { renderStreamMessage } from "@hotwired/turbo"

export default class extends Base {
  static targets = [
    "search", "filterBtn", "sort", "sortDropdown", "sortPanel", "sortLabel", "sortOption",
    "minPrice", "maxPrice", "priceTrack", "minPriceDisplay", "maxPriceDisplay",
    "durationBtn", "paxBtn",
    "venueBtn", "dronePointBtn", "dpFilterSection", "dpPillContainer",
    "minPriceInput", "maxPriceInput"
  ]
  static values = {
    url:              String,
    boundsMin:        Number,
    boundsMax:        Number,
    initialCategory:  { type: String, default: "All" },
    initialDuration:  { type: String, default: "" },
    initialPax:       { type: String, default: "" },
    initialVenue:     { type: String, default: "" },
    initialDronePoint:{ type: String, default: "" }
  }

  connect() {
    this.activeCategory   = this.initialCategoryValue
    this.activeDuration   = this.initialDurationValue
    this.activePax        = this.initialPaxValue
    this.activeVenue      = this.initialVenueValue
    this.activeDronePoint = this.initialDronePointValue
    this.debounceTimer    = null
    this.updatePriceTrack()
    this.updatePriceDisplays()
  }

  // ── Category ──────────────────────────────────────────────
  setCategory(event) {
    this.activeCategory = event.currentTarget.dataset.category
    this.updateCategoryStyles()
    this.submit()
  }

  // ── Duration ──────────────────────────────────────────────
  setDuration(event) {
    const val = event.currentTarget.dataset.duration
    this.activeDuration = this.activeDuration === val ? "" : val
    this.updateDurationStyles()
    this.submit()
  }

  // ── Venue ─────────────────────────────────────────────────
  setVenue(event) {
    this.activeVenue      = event.currentTarget.dataset.venue || ""
    this.activeDronePoint = ""
    this.updateVenueStyles()
    this.updateDronePointStyles()
    this._toggleDpSection()
    this.submit()
  }

  // ── Drone point ───────────────────────────────────────────
  setDronePoint(event) {
    const val = event.currentTarget.dataset.dronePoint || ""
    this.activeDronePoint = this.activeDronePoint === val ? "" : val
    this.updateDronePointStyles()
    this.submit()
  }

  // ── Max pax ───────────────────────────────────────────────
  setPax(event) {
    const val = event.currentTarget.dataset.pax
    this.activePax = this.activePax === val ? "" : val
    this.updatePaxStyles()
    this.submit()
  }

  // ── Search / Sort ─────────────────────────────────────────
  onSearch() { this.submit() }

  toggleSort(event) {
    event.stopPropagation()
    const panel  = this.sortPanelTarget
    const isOpen = !panel.classList.contains("opacity-0")
    if (isOpen) {
      this._closeSortPanel()
    } else {
      panel.classList.remove("opacity-0", "pointer-events-none", "scale-95")
      panel.classList.add("opacity-100", "pointer-events-auto", "scale-100")
    }
  }

  selectSort(event) {
    const btn   = event.currentTarget
    const value = btn.dataset.sortValue
    const label = btn.dataset.sortLabel
    this.sortTarget.value         = value
    this.sortLabelTarget.textContent = label
    this.updateSortOptionStyles(value)
    this._closeSortPanel()
    this.submit()
  }

  closeSortPanel(event) {
    if (this.hasSortDropdownTarget && !this.sortDropdownTarget.contains(event.target)) {
      this._closeSortPanel()
    }
  }

  _closeSortPanel() {
    const panel = this.sortPanelTarget
    panel.classList.add("opacity-0", "pointer-events-none", "scale-95")
    panel.classList.remove("opacity-100", "pointer-events-auto", "scale-100")
  }

  updateSortOptionStyles(activeValue) {
    this.sortOptionTargets.forEach(btn => {
      const active = btn.dataset.sortValue === activeValue
      btn.className = `w-full flex items-center justify-between px-4 py-2.5 text-sm transition hover:bg-light ${active ? "font-semibold text-navy" : "text-foreground"}`
      const check = btn.querySelector("svg")
      if (active && !check) {
        btn.insertAdjacentHTML("beforeend", `<svg class="h-4 w-4 text-navy shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>`)
      } else if (!active && check) {
        check.remove()
      }
    })
  }

  // ── Price range ───────────────────────────────────────────
  onMinPrice() {
    const min = parseInt(this.minPriceTarget.value)
    const max = parseInt(this.maxPriceTarget.value)
    if (min > max) this.minPriceTarget.value = max
    this.updatePriceTrack()
    this.updatePriceDisplays()
    this.submit()
  }

  onMaxPrice() {
    const min = parseInt(this.minPriceTarget.value)
    const max = parseInt(this.maxPriceTarget.value)
    if (max < min) this.maxPriceTarget.value = min
    this.updatePriceTrack()
    this.updatePriceDisplays()
    this.submit()
  }

  onMinPriceInput() {
    if (!this.hasMinPriceInputTarget || !this.hasMinPriceTarget) return
    let val = parseInt(this.minPriceInputTarget.value) || this.boundsMinValue
    val = Math.max(this.boundsMinValue, Math.min(val, parseInt(this.maxPriceTarget.value)))
    this.minPriceInputTarget.value  = val
    this.minPriceTarget.value       = val
    this.updatePriceTrack()
    this.submit()
  }

  onMaxPriceInput() {
    if (!this.hasMaxPriceInputTarget || !this.hasMaxPriceTarget) return
    let val = parseInt(this.maxPriceInputTarget.value) || this.boundsMaxValue
    val = Math.min(this.boundsMaxValue, Math.max(val, parseInt(this.minPriceTarget.value)))
    this.maxPriceInputTarget.value  = val
    this.maxPriceTarget.value       = val
    this.updatePriceTrack()
    this.submit()
  }

  updatePriceTrack() {
    if (!this.hasPriceTrackTarget || !this.hasMinPriceTarget) return
    const min   = parseInt(this.minPriceTarget.value)
    const max   = parseInt(this.maxPriceTarget.value)
    const range = this.boundsMaxValue - this.boundsMinValue
    if (range === 0) return
    const leftPct  = ((min - this.boundsMinValue) / range) * 100
    const rightPct = 100 - ((max - this.boundsMinValue) / range) * 100
    this.priceTrackTarget.style.left  = leftPct + "%"
    this.priceTrackTarget.style.right = rightPct + "%"
  }

  updatePriceDisplays() {
    if (!this.hasMinPriceTarget) return
    const min = parseInt(this.minPriceTarget.value)
    const max = parseInt(this.maxPriceTarget.value)
    if (this.hasMinPriceDisplayTarget) this.minPriceDisplayTarget.textContent = this.formatIDR(min)
    if (this.hasMaxPriceDisplayTarget) this.maxPriceDisplayTarget.textContent = this.formatIDR(max)
    if (this.hasMinPriceInputTarget)   this.minPriceInputTarget.value = min
    if (this.hasMaxPriceInputTarget)   this.maxPriceInputTarget.value = max
  }

  formatIDR(value) {
    return new Intl.NumberFormat("id-ID", {
      style: "currency", currency: "IDR", minimumFractionDigits: 0
    }).format(value)
  }

  // ── Clear all ─────────────────────────────────────────────
  clearFilters() {
    this.activeCategory   = "All"
    this.activeDuration   = ""
    this.activePax        = ""
    this.activeVenue      = ""
    this.activeDronePoint = ""
    if (this.hasSearchTarget)    this.searchTarget.value      = ""
    if (this.hasSortTarget)      this.sortTarget.value        = "newest"
    if (this.hasSortLabelTarget) this.sortLabelTarget.textContent = "Newest"
    this._closeSortPanel()
    this.updateSortOptionStyles("newest")
    if (this.hasMinPriceTarget) {
      this.minPriceTarget.value = this.boundsMinValue
      this.maxPriceTarget.value = this.boundsMaxValue
    }
    this.updateCategoryStyles()
    this.updateDurationStyles()
    this.updatePaxStyles()
    this.updateVenueStyles()
    this.updateDronePointStyles()
    this._toggleDpSection()
    this.updatePriceTrack()
    this.updatePriceDisplays()
    this.fetchResults()
  }

  // ── Style helpers ─────────────────────────────────────────
  updateCategoryStyles() {
    this.filterBtnTargets.forEach(btn => {
      const active = btn.dataset.category === this.activeCategory
      btn.className = active
        ? "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition bg-navy text-white"
        : "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition bg-light text-muted-foreground hover:bg-surface hover:text-foreground"
      const badge = btn.querySelector("span")
      if (badge) {
        badge.className = active
          ? "rounded-full px-1.5 text-xs bg-white/15 text-white"
          : "rounded-full px-1.5 text-xs bg-background text-foreground/60"
      }
    })
  }

  updateDurationStyles() {
    this.durationBtnTargets.forEach(btn => {
      const active = btn.dataset.duration === this.activeDuration
      btn.className = active
        ? "rounded-lg px-3 py-1.5 text-xs font-semibold transition bg-navy text-white"
        : "rounded-lg px-3 py-1.5 text-xs font-semibold transition bg-light text-muted-foreground hover:bg-surface hover:text-foreground"
    })
  }

  updatePaxStyles() {
    this.paxBtnTargets.forEach(btn => {
      const active = btn.dataset.pax === this.activePax
      btn.className = active
        ? "rounded-lg px-3 py-1.5 text-xs font-semibold transition bg-navy text-white"
        : "rounded-lg px-3 py-1.5 text-xs font-semibold transition bg-light text-muted-foreground hover:bg-surface hover:text-foreground"
    })
  }

  updateVenueStyles() {
    this.venueBtnTargets.forEach(btn => {
      const active = (btn.dataset.venue || "") === this.activeVenue
      btn.className = btn.className
        .replace(/bg-navy text-white|bg-light text-muted-foreground hover:bg-surface hover:text-foreground/g, "")
        .trim() + " " + (active
          ? "bg-navy text-white"
          : "bg-light text-muted-foreground hover:bg-surface hover:text-foreground")
    })
  }

  updateDronePointStyles() {
    this.dronePointBtnTargets.forEach(btn => {
      const active = (btn.dataset.dronePoint || "") === this.activeDronePoint
      btn.className = btn.className
        .replace(/bg-navy text-white|bg-light text-muted-foreground hover:bg-surface hover:text-foreground/g, "")
        .trim() + " " + (active
          ? "bg-navy text-white"
          : "bg-light text-muted-foreground hover:bg-surface hover:text-foreground")
    })
  }

  _toggleDpSection() {
    if (!this.hasDpFilterSectionTarget) return
    const show = this.activeVenue !== ""
    this.dpFilterSectionTarget.classList.toggle("hidden", !show)
  }

  // ── Fetch ─────────────────────────────────────────────────
  submit() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetchResults(), 350)
  }

  async fetchResults() {
    const url = new URL(this.urlValue, window.location.origin)
    const p   = url.searchParams

    // Only include non-default values so the URL stays clean on refresh
    const search = this.hasSearchTarget ? this.searchTarget.value.trim() : ""
    if (search) p.set("q[name_or_description_cont]", search)

    const sort = this.hasSortTarget ? this.sortTarget.value : "newest"
    if (sort && sort !== "newest") p.set("q[sorted_by]", sort)

    if (this.activeCategory && this.activeCategory !== "All") {
      p.set("category", this.activeCategory)
    }

    if (this.hasMinPriceTarget) {
      const min = parseInt(this.minPriceTarget.value)
      const max = parseInt(this.maxPriceTarget.value)
      if (min > this.boundsMinValue) p.set("min_price", min)
      if (max < this.boundsMaxValue) p.set("max_price", max)
    }

    if (this.activeDuration)   p.set("duration",    this.activeDuration)
    if (this.activePax)        p.set("min_pax",     this.activePax)
    if (this.activeVenue)      p.set("venue",        this.activeVenue)
    if (this.activeDronePoint) p.set("drone_point",  this.activeDronePoint)

    // Persist current filters in the address bar (clean, no format param)
    history.replaceState(null, "", url.toString())

    this.showSkeleton()

    const response = await fetch(url.toString(), {
      headers: { Accept: "text/vnd.turbo-stream.html" }
    })
    const html = await response.text()
    renderStreamMessage(html)
  }

  showSkeleton() {
    const grid = document.getElementById("packages-grid")
    if (!grid) return
    const card = `
      <div class="flex flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-sm animate-pulse">
        <div class="aspect-16/10 bg-light"></div>
        <div class="flex flex-1 flex-col gap-3 p-5">
          <div class="h-5 rounded-full bg-light w-3/4"></div>
          <div class="space-y-2">
            <div class="h-3 rounded-full bg-light w-full"></div>
            <div class="h-3 rounded-full bg-light w-2/3"></div>
          </div>
          <div class="flex gap-4">
            <div class="h-3 rounded-full bg-light w-1/4"></div>
            <div class="h-3 rounded-full bg-light w-1/3"></div>
          </div>
          <div class="mt-auto pt-4 space-y-3">
            <div class="h-4 rounded-full bg-light w-1/3"></div>
            <div class="h-11 rounded-xl bg-light w-full"></div>
          </div>
        </div>
      </div>`
    grid.innerHTML = Array(6).fill(card).join("")
  }
}
